import { useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import ProductCard from "@/components/feature/ProductCard";
import SmartImage from "@/components/base/SmartImage";
import { getProduct, products } from "@/mocks/products";
import { useCart } from "@/hooks/useCart";

export default function ProductPage() {
  const { slug = "" } = useParams();
  const product = getProduct(slug);
  const cart = useCart();
  const [color, setColor] = useState(product?.colorways[0]?.name ?? "");
  const [size, setSize] = useState(product?.sizes?.[2] ?? product?.sizes?.[0] ?? "");
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);

  const activeColorway = product?.colorways.find((c) => c.name === color) ?? product?.colorways[0];
  const mainImg = activeColorway?.img ?? product?.img ?? "";

  const related = useMemo(() => {
    if (!product) return [];
    return products.filter((p) => p.slug !== product.slug && p.categorySlug === product.categorySlug).slice(0, 4).concat(
      products.filter((p) => p.slug !== product.slug && p.categorySlug !== product.categorySlug).slice(0, 4)
    ).slice(0, 4);
  }, [product]);

  if (!product) return <Navigate to="/shop" replace />;

  const handleAdd = () => {
    cart.add({
      slug: product.slug,
      name: product.name,
      price: product.price,
      img: mainImg,
      color,
      size: product.sizes ? size : undefined,
      quantity: qty,
    });
  };

  return (
    <div className="min-h-screen bg-white text-neutral-800 flex flex-col">
      <SiteHeader variant="solid" />
      <main className="flex-1 pt-[72px] md:pt-20 bg-white">
        {/* Breadcrumb */}
        <div className="w-full px-5 md:px-10 pt-6 pb-2 text-[11px] tracking-[0.15em] uppercase text-neutral-500">
          <Link to="/shop" className="hover:text-neutral-900 cursor-pointer">Shop</Link>
          <span className="mx-2">/</span>
          <Link to={`/shop/${product.categorySlug}`} className="hover:text-neutral-900 cursor-pointer">{product.category}</Link>
          <span className="mx-2">/</span>
          <span className="text-neutral-800">{product.name}</span>
        </div>

        {/* Purchase zone */}
        <section className="w-full px-5 md:px-10 py-6 md:py-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            {/* Gallery */}
            <div className="lg:col-span-7">
              <div className="relative w-full aspect-square md:aspect-auto md:h-[620px] bg-white overflow-hidden">
                <SmartImage
                  src={activeImg === 0 ? mainImg : (product.gallery[activeImg] || mainImg)}
                  alt={`${product.name} — ${activeColorway?.name ?? ""}`}
                  tone="light"
                  eager
                  imgClassName={`w-full h-full object-center ${activeImg === 0 ? "object-contain" : "object-cover"}`}
                  imgStyle={activeImg === 0 ? { transform: `scale(${product.scale ?? 1})` } : undefined}
                />
              </div>
              {product.gallery.length > 1 && (
                <div className="flex gap-3 mt-4">
                  {product.gallery.map((g, i) => (
                    <button
                      key={g}
                      onClick={() => setActiveImg(i)}
                      className={`relative w-20 h-24 bg-white overflow-hidden border cursor-pointer ${i === activeImg ? "border-neutral-900" : "border-neutral-200"}`}
                      aria-label={`View ${i + 1}`}
                    >
                      <SmartImage src={g} alt="" tone="light" imgClassName="w-full h-full object-contain object-center" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Buy panel */}
            <div className="lg:col-span-5 lg:pl-6">
              <p className="text-[10px] tracking-[0.24em] uppercase text-neutral-500 mb-2">{product.category}</p>
              <h1 className="text-[28px] md:text-[32px] leading-[1.12] md:leading-[1.1] tracking-[-0.01em] text-neutral-900 font-light mb-3">
                {product.name}
              </h1>
              <p className="text-[14px] md:text-[13px] text-neutral-600 mb-4">{product.descriptor}</p>
              <p className="text-[18px] text-neutral-900 mb-8">${product.price}</p>

              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[11px] tracking-[0.24em] uppercase text-neutral-500">Color</p>
                  <p className="text-[11px] text-neutral-600">{color}</p>
                </div>
                <div className="flex items-center gap-3">
                  {product.colorways.map((s) => (
                    <button
                      key={s.name}
                      onClick={() => { setColor(s.name); setActiveImg(0); }}
                      className={`w-8 h-8 rounded-full border-2 transition cursor-pointer ${
                        color === s.name ? "border-neutral-900" : "border-neutral-200 hover:border-neutral-400"
                      }`}
                      title={s.name}
                      style={{ backgroundColor: s.hex }}
                    />
                  ))}
                </div>
              </div>

              {product.sizes && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-[11px] tracking-[0.24em] uppercase text-neutral-500">Size</p>
                    <button className="text-[11px] text-neutral-600 hover:text-neutral-900 underline underline-offset-4 cursor-pointer">Size guide</button>
                  </div>
                  <div className="grid grid-cols-6 gap-2">
                    {product.sizes.map((s) => (
                      <button
                        key={s}
                        onClick={() => setSize(s)}
                        className={`py-2.5 text-[12px] rounded-md border transition cursor-pointer ${
                          size === s
                            ? "bg-neutral-900 text-white border-neutral-900"
                            : "border-neutral-200 text-neutral-700 hover:border-neutral-400"
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3 mb-8">
                <div className="flex items-center border border-neutral-200 rounded-full">
                  <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-11 h-12 md:w-9 md:h-10 flex items-center justify-center text-neutral-600 hover:text-neutral-900 cursor-pointer" aria-label="Decrease">
                    <i className="ri-subtract-line"></i>
                  </button>
                  <span className="w-8 text-center text-[14px] md:text-[13px]">{qty}</span>
                  <button onClick={() => setQty(qty + 1)} className="w-11 h-12 md:w-9 md:h-10 flex items-center justify-center text-neutral-600 hover:text-neutral-900 cursor-pointer" aria-label="Increase">
                    <i className="ri-add-line"></i>
                  </button>
                </div>
                <button
                  onClick={handleAdd}
                  className="flex-1 inline-flex items-center justify-center gap-2 h-12 md:h-10 px-6 bg-primary-500 hover:bg-primary-600 text-white text-[14px] md:text-[13px] rounded-full transition cursor-pointer whitespace-nowrap"
                >
                  Add to Bag · ${product.price * qty}
                </button>
              </div>

              <p className="text-[12px] text-neutral-500 leading-relaxed">
                Free shipping over $150 · 30-day returns · Lifetime repair program.
              </p>
            </div>
          </div>
        </section>

        {/* Story + material */}
        <section className="bg-neutral-50 py-12 md:py-20">
          <div className="w-full px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-10 lg:gap-16">
            <div className="lg:col-span-5">
              <p className="text-[10px] tracking-[0.24em] uppercase text-neutral-500 mb-3">Product story</p>
              <h2 className="text-[24px] md:text-[28px] leading-[1.18] md:leading-[1.15] tracking-[-0.01em] text-neutral-900 font-light">
                {product.story}
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 space-y-6">
              <div>
                <p className="text-[11px] tracking-[0.24em] uppercase text-neutral-500 mb-2">Material</p>
                <p className="text-[15px] md:text-[14px] text-neutral-700 leading-relaxed">{product.material}</p>
              </div>
              <div>
                <p className="text-[11px] tracking-[0.24em] uppercase text-neutral-500 mb-2">Details</p>
                <ul className="text-[13px] text-neutral-700 space-y-2">
                  {product.details.map((d) => (
                    <li key={d} className="flex gap-3">
                      <span className="text-neutral-400">—</span>
                      <span>{d}</span>
                    </li>
                  ))}
                </ul>
              </div>
              {product.tech.length > 0 && (
                <div>
                  <p className="text-[11px] tracking-[0.24em] uppercase text-neutral-500 mb-2">Technology</p>
                  <div className="flex flex-wrap gap-2">
                    {product.tech.map((t) => (
                      <Link key={t} to="/technology" className="text-[11px] tracking-[0.14em] uppercase px-3 py-1.5 rounded-full border border-neutral-200 text-neutral-700 hover:text-neutral-900 hover:border-neutral-400 cursor-pointer">
                        {t}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {product.gallery[1] && (
          <section className="relative w-full h-[260px] md:h-[520px] overflow-hidden bg-neutral-100">
            <SmartImage src={product.gallery[1]} alt="In-use" tone="light" imgClassName="w-full h-full object-cover object-center" />
          </section>
        )}

        {/* Related */}
        <section className="bg-white text-neutral-900 py-12 md:py-16">
          <div className="w-full px-5 md:px-10">
            <div className="flex items-end justify-between mb-8">
              <h2 className="text-[22px] md:text-[26px] leading-[1.1] tracking-[-0.01em] font-light text-neutral-900">You might also consider.</h2>
              <Link to="/shop" className="text-[12px] text-neutral-700 hover:text-primary-600 border-b border-neutral-300 pb-1 cursor-pointer">
                View all
              </Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 md:gap-x-10 gap-y-8 md:gap-y-12 max-w-[1600px] mx-auto" data-product-shop>
              {related.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}